/*
GraderChannel subscribes to the request's Ably channel to receive the grading
outcome. If Ably fails, it falls back to polling the grader's DB. Both paths
normalize to a shared GradingOutcome before invoking the callback.
*/

import type { RealtimeChannel } from "ably";
import { ChannelProvider, useConnectionStateListener } from "ably/react";
import { useCallback, useEffect, useRef } from "react";
import { servicesOrchestratorValues } from "@/config/services";
import logger from "@/lib/logging/logger";
import { graderApi } from "@/services";
import { type GradingOutcome, outcomeFromPollingResults, parseAblyGradingMessage } from "@/services/grader/outcome";
import { useGraderChannel } from "../hooks";

const POLLING_FALLBACK_INTERVAL_MS = 5000;

type GraderChannelProps = {
	channelId: string;
	cb: (outcome: GradingOutcome) => void | Promise<void>;
	/** The grader did not answer within the polling budget; no outcome will follow. */
	onGiveUp: () => void;
};

const AblyChannel = ({ channelId, cb, onGiveUp }: GraderChannelProps) => {
	const interval = useRef<ReturnType<typeof setInterval> | undefined>(undefined);
	const timeout = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
	const isFetching = useRef(false);
	const fallbackStarted = useRef(false);
	const outcomeHandled = useRef(false);
	const pollAttempts = useRef(0);
	const channelRef = useRef<RealtimeChannel | null>(null);
	const cbRef = useRef(cb);
	const onGiveUpRef = useRef(onGiveUp);
	const gradeResponseTimeout = servicesOrchestratorValues.app.gradeResponseTimeoutMs;
	cbRef.current = cb;
	onGiveUpRef.current = onGiveUp;
	// The fallback polls for one full grading allowance after it starts; past that the
	// grader is not going to answer, and the page is told so rather than left waiting.
	// The count follows the configured allowance on purpose: a fixed number at this
	// cadence gives up before the grader's window ends on every longer configuration.
	const maxPollingAttempts = Math.ceil(gradeResponseTimeout / POLLING_FALLBACK_INTERVAL_MS);

	// Must run before the duplicate guard below: a poller created after the first outcome
	// is only reachable from here, and the page can stay mounted long after it.
	const stopPolling = useCallback(() => {
		if (interval.current) {
			clearInterval(interval.current);
			interval.current = undefined;
		}
		if (timeout.current) {
			clearTimeout(timeout.current);
			timeout.current = undefined;
		}
	}, []);

	const handleOutcome = useCallback(
		async (outcome: GradingOutcome) => {
			stopPolling();

			if (outcomeHandled.current) {
				logger.info("INFO", "Ignoring duplicate grader outcome", { channelId });
				return;
			}

			outcomeHandled.current = true;
			logger.info("INFO", "Got grader outcome", { outcome });
			channelRef.current?.off();
			await cbRef.current(outcome);
		},
		[channelId, stopPolling],
	);

	const { channel } = useGraderChannel(channelId, async (message: unknown) => {
		const msg = message as { data?: unknown };
		logger.info("INFO", "Message retrieved in channel", msg);
		const outcome = parseAblyGradingMessage(msg.data);
		if (!outcome) return;
		await handleOutcome(outcome);
	});

	channelRef.current = channel;

	const retrieveFromGrader = useCallback(async () => {
		if (isFetching.current || outcomeHandled.current) return;
		if (pollAttempts.current >= maxPollingAttempts) {
			stopPolling();
			outcomeHandled.current = true;
			channelRef.current?.off();
			logger.error("FLOW", "Giving up on the grade response after the polling budget", {
				channelId,
				attempts: pollAttempts.current,
			});
			onGiveUpRef.current();
			return;
		}
		try {
			isFetching.current = true;
			pollAttempts.current++;
			const graderResponse = await graderApi.getGradeResponse(channelId);
			// The request cannot be cancelled, so an outcome delivered over Ably while it was
			// in flight has to be re-checked here — otherwise this poll reports it a second
			// time and the duplicate is one we caused ourselves.
			if (outcomeHandled.current) return;
			if (graderResponse.status === "completed") {
				await handleOutcome(outcomeFromPollingResults(graderResponse.results));
			}
		} catch (error) {
			logger.error("INFO", "Could not retrieve grade request", error);
		} finally {
			isFetching.current = false;
		}
	}, [channelId, handleOutcome, maxPollingAttempts, stopPolling]);

	const enablePollingFallback = useCallback(async () => {
		if (fallbackStarted.current) return;
		fallbackStarted.current = true;

		if (timeout.current) {
			clearTimeout(timeout.current);
			timeout.current = undefined;
		}

		await retrieveFromGrader();
		// The awaited poll above can already have delivered the outcome; scheduling the
		// interval regardless creates one that nothing is left to clear.
		if (outcomeHandled.current) return;
		interval.current = setInterval(retrieveFromGrader, POLLING_FALLBACK_INTERVAL_MS);
	}, [retrieveFromGrader]);

	useEffect(() => {
		timeout.current = setTimeout(async () => {
			logger.warning("INFO", "Grader response is taking longer than expected..", {
				name: "Grade request elapsed time",
				elapsed_time_s: gradeResponseTimeout / 1000,
			});
			clearTimeout(timeout.current);
			timeout.current = undefined;
			enablePollingFallback();
		}, gradeResponseTimeout);

		return () => {
			channelRef.current?.detach();
			timeout.current && clearTimeout(timeout.current);
			interval.current && clearInterval(interval.current);
		};
	}, [enablePollingFallback, gradeResponseTimeout]);

	useConnectionStateListener(["failed", "suspended"], (stateChange) => {
		logger.warning("INFO", `Ably connection ${stateChange.current}, falling back to polling`, {
			reason: stateChange.reason?.message,
		});
		enablePollingFallback();
	});

	useEffect(() => {
		channel?.on(
			["attached", "attaching", "detaching", "detached", "failed", "suspended", "initialized", "update"],
			(data) => {
				logger.info("INFO", "Ably channel event", data);
			},
		);

		return () => {
			channel?.off();
		};
	}, [channel]);

	return <></>;
};

export function GraderChannel({ channelId, cb, onGiveUp }: GraderChannelProps) {
	return (
		<ChannelProvider channelName={channelId}>
			<AblyChannel
				channelId={channelId}
				cb={cb}
				onGiveUp={onGiveUp}
			/>
		</ChannelProvider>
	);
}
